import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { clone as cloneSkinned } from 'three/examples/jsm/utils/SkeletonUtils.js';

import {
  ASSET_MANIFEST, type AssetEntry, type FitMode, type MaterialOverrides,
} from './manifest.ts';
import {
  ASSET_ROLES, FALLBACKS, type AnimState, type AssetRole, type CreateRequest,
} from './roles.ts';
import type { Faction } from '../../core/constants.ts';

/**
 * Fetches and parses one model.
 *
 * Injectable so the registry can be exercised without a network or a real file:
 * the anchoring and fitting below are the most error-prone code in the layer,
 * and their failures are silent — a mage buried to the waist, a pillar the size
 * of the cathedral. Testing them against GLTFLoader would be testing Three.
 */
/** Fetches one texture. Injectable for the same reason the model loader is. */
export type TextureLoad = (url: string) => Promise<THREE.Texture>;

const defaultTextureLoad: TextureLoad = (url) => new THREE.TextureLoader().loadAsync(url);

export type ModelLoader = (url: string) => Promise<{
  scene?: THREE.Object3D;
  scenes?: THREE.Object3D[];
  animations?: THREE.AnimationClip[];
}>;

const gltf: ModelLoader = (url) => new GLTFLoader().loadAsync(url);

/** Where a role's geometry came from, and why, for the console and for tests. */
export interface RoleReport {
  role: AssetRole;
  source: 'model' | 'fallback';
  url?: string;
  problem?: string;
}

interface Loaded {
  scene: THREE.Object3D;
  clips: THREE.AnimationClip[];
  entry: AssetEntry;
  skinned: boolean;
}

/** Animation files, cached by url so four mages share one download. */
type ClipLibrary = Map<string, THREE.AnimationClip[]>;

/**
 * Hands out geometry by role.
 *
 * The registry is the only place that knows an art pack exists. It loads what
 * the manifest names, normalises it — scale, axis convention, and anchoring at
 * the base so a call site can place a model and a capsule identically — and
 * hands back clones. Anything it cannot load falls back to the gray-box
 * primitive, loudly in the console and quietly on screen.
 *
 * Nothing here touches physics. Colliders are built from `LevelGeometry`, which
 * is core data, so art can never change where a wall is or what a spell hits.
 * That separation is what makes an art pass a purely visual risk, and it is
 * worth keeping deliberately rather than by accident.
 */
/**
 * Gives a cloned object its own copy of every material it uses.
 *
 * One clone per *distinct* source material rather than per mesh: a model whose
 * cloak and hood share a material should go on sharing it within the instance,
 * or dimming a ghost would take two passes to do one job and the two could
 * disagree.
 */
function isolateMaterials(object: THREE.Object3D): void {
  const mine = new Map<THREE.Material, THREE.Material>();
  const own = (m: THREE.Material): THREE.Material => {
    const had = mine.get(m);
    if (had) return had;
    const copy = m.clone();
    mine.set(m, copy);
    return copy;
  };

  object.traverse((node) => {
    const mesh = node as THREE.Mesh;
    if (!mesh.isMesh || !mesh.material) return;
    mesh.material = Array.isArray(mesh.material)
      ? mesh.material.map(own)
      : own(mesh.material);
  });
}

export class AssetRegistry {
  private readonly loaded = new Map<AssetRole, Loaded>();
  private readonly libraries: ClipLibrary = new Map();
  /** Faction textures, cached by url so three mages of a side share one upload. */
  private readonly skins = new Map<string, THREE.Texture>();
  /** Faction textures somebody needed that were never loaded, warned about once each. */
  private readonly missingSkins = new Set<string>();
  private readonly reports: RoleReport[] = [];

  private constructor() {}

  /**
   * Loads everything the manifest names. Failures are reported and survived —
   * a missing or malformed model must never stop a match from starting.
   */
  static async load(
    manifest: Partial<Record<AssetRole, AssetEntry>> = ASSET_MANIFEST,
    load: ModelLoader = gltf,
  ): Promise<AssetRegistry> {
    const registry = new AssetRegistry();

    const entries = ASSET_ROLES
      .map((role) => [role, manifest[role]] as const)
      .filter((pair): pair is readonly [AssetRole, AssetEntry] => pair[1] !== undefined);

    // Shared clip files first, and only once each however many roles name them.
    const libraries = new Set(
      entries.map(([, entry]) => entry.animationsFrom).filter((u): u is string => !!u),
    );
    await Promise.all([...libraries].map(async (url) => {
      try {
        const model = await load(url);
        registry.libraries.set(url, model.animations ?? []);
      } catch (err) {
        console.warn(`[assets] animation library ${url} failed: ${String(err)}`);
        registry.libraries.set(url, []);
      }
    }));

    await Promise.all(entries.map(async ([role, entry]) => {
      try {
        const model = await load(entry.url);
        const scene = model.scene ?? model.scenes?.[0];
        if (!scene) throw new Error('the file contains no scene');

        if (entry.materials) applyMaterials(scene, entry.materials);
        const anchored = normalise(scene, entry);
        let skinned = false;
        anchored.traverse((o) => {
          if ((o as THREE.SkinnedMesh).isSkinnedMesh) skinned = true;
        });

        registry.loaded.set(role, {
          scene: anchored, clips: model.animations ?? [], entry, skinned,
        });
        registry.reports.push({ role, source: 'model', url: entry.url });
      } catch (err) {
        const problem = err instanceof Error ? err.message : String(err);
        console.warn(`[assets] ${role} falling back to gray-box: ${problem}`);
        registry.reports.push({ role, source: 'fallback', url: entry.url, problem });
      }
    }));

    for (const role of ASSET_ROLES) {
      if (registry.reports.some((r) => r.role === role)) continue;
      registry.reports.push({ role, source: 'fallback' });
    }
    return registry;
  }

  /**
   * Fetches the faction textures for the factions actually playing.
   *
   * Deliberately not all twelve: a match with two sides in it has no use for the
   * third's wardrobe, and each 1K texture is four megabytes of video memory once
   * uploaded.
   */
  async loadFactionTextures(
    factions: readonly Faction[],
    load: TextureLoad = defaultTextureLoad,
  ): Promise<void> {
    const wanted = new Set<string>();
    for (const entry of this.loaded.values()) {
      for (const byFaction of Object.values(entry.entry.factionTextures ?? {})) {
        for (const faction of factions) {
          const url = byFaction[faction];
          if (url) wanted.add(url);
        }
      }
    }

    await Promise.all([...wanted].map(async (url) => {
      try {
        const texture = await load(url);
        // Base colour maps are authored in sRGB; sampled as linear they come out
        // washed out and pale, which is a very confusing thing to debug.
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.flipY = false;
        this.skins.set(url, texture);
      } catch (err) {
        console.warn(`[assets] faction texture ${url} failed: ${String(err)}`);
      }
    }));
  }

  /** A registry with no art at all. What the tests and a bare checkout use. */
  static grayBox(): AssetRegistry {
    const registry = new AssetRegistry();
    for (const role of ASSET_ROLES) registry.reports.push({ role, source: 'fallback' });
    return registry;
  }

  /**
   * Geometry for a role, anchored at the centre of its base. Never null: a role
   * with no model behind it returns its fallback, so no call site needs a branch.
   */
  create(req: CreateRequest): THREE.Object3D {
    const loaded = this.loaded.get(req.role);
    if (!loaded) return FALLBACKS[req.role](req);

    const copy = loaded.skinned ? cloneSkinned(loaded.scene) : loaded.scene.clone(true);
    /*
     * The clone gets its own materials, because `clone` does not give it any.
     *
     * Three.js clones the object graph and leaves every mesh pointing at the
     * *same* material instances as the source. So anything that edits a material
     * in place edits it for every mage in the match, and two things do: `tint`
     * below, and the dimming a ghost gets. The symptoms were a whole arena
     * dressed in whichever faction happened to be created last — red, because
     * Chaos is last — and living mages turning translucent whenever somebody
     * else died.
     *
     * `dress` already clones the materials it swaps, which is why faction
     * *textures* were right on the few garments that had them and nothing else
     * was. Doing it up front makes the rule one rule: an instance owns its
     * materials, and nothing has to remember to clone before writing.
     *
     * Cheap. A material clone is a small object, and Three.js keys its compiled
     * programs on the material's *parameters* rather than its identity, so two
     * clones of the same material share a program and nothing recompiles.
     */
    isolateMaterials(copy);
    // Texture first, tint second: a garment given a faction texture of its own
    // has no need of a multiply on top, and one would only muddy it.
    const dressed = req.faction
      ? this.dress(copy, loaded.entry, req.faction)
      : new Set<string>();
    if (req.color !== undefined) tint(copy, req.color, loaded.entry, dressed);
    fitInto(copy, loaded.entry.fit ?? 'none', req.size);
    return copy;
  }

  /**
   * This role's clips under the names the game asks for, from wherever they
   * live — the model's own file, or a shared library it points at.
   */
  clipsFor(role: AssetRole): Partial<Record<AnimState, THREE.AnimationClip>> {
    const loaded = this.loaded.get(role);
    if (!loaded?.entry.clips) return {};

    const source = loaded.entry.animationsFrom
      ? this.libraries.get(loaded.entry.animationsFrom) ?? []
      : loaded.clips;

    const out: Partial<Record<AnimState, THREE.AnimationClip>> = {};
    for (const [state, name] of Object.entries(loaded.entry.clips)) {
      const clip = source.find((c) => c.name === name);
      if (clip) out[state as AnimState] = clip;
      else console.warn(`[assets] ${role} has no clip named "${name}"`);
    }
    return out;
  }

  /**
   * What each of this role's clips was authored to move at, metres per second.
   *
   * Empty when the manifest says nothing, which the animator reads as "play it
   * straight" — the behaviour every pack had before the table existed.
   */
  clipSpeedsFor(role: AssetRole): Partial<Record<AnimState, number>> {
    return this.loaded.get(role)?.entry.clipSpeeds ?? {};
  }

  /**
   * Swaps in this faction's textures, and reports which materials it dressed so
   * the tint knows to leave them alone.
   */
  private dress(object: THREE.Object3D, entry: AssetEntry, faction: Faction): Set<string> {
    const done = new Set<string>();
    const wardrobe = entry.factionTextures;
    if (!wardrobe) return done;

    object.traverse((node) => {
      const mesh = node as THREE.Mesh;
      if (!mesh.isMesh) return;

      const swap = (m: THREE.Material): THREE.Material => {
        const prefix = Object.keys(wardrobe).find((k) => m.name?.startsWith(k));
        if (!prefix) return m;
        const url = wardrobe[prefix][faction];
        const texture = url ? this.skins.get(url) : undefined;
        if (!texture) {
          /*
           * Said out loud, once. A garment with no texture for its side is drawn
           * in whatever the model was exported wearing — another faction's
           * colours, as often as not — and nothing on screen says why.
           */
          if (url && !this.missingSkins.has(url)) {
            this.missingSkins.add(url);
            console.warn(`[assets] ${faction} has no texture for ${prefix}: ${url} was not loaded`);
          }
          return m;
        }

        const copy = m.clone() as THREE.MeshStandardMaterial;
        copy.map = texture;
        copy.needsUpdate = true;
        done.add(prefix);
        return copy;
      };

      mesh.material = Array.isArray(mesh.material)
        ? mesh.material.map(swap)
        : swap(mesh.material);
    });
    return done;
  }

  usesModel(role: AssetRole): boolean {
    return this.loaded.has(role);
  }

  report(): readonly RoleReport[] {
    return this.reports;
  }
}

/**
 * Applies the manifest's own corrections, then re-anchors the model so its
 * origin sits at the centre of its base.
 *
 * Anchoring is what lets a call site place a loaded mesh and a fallback capsule
 * with the same line. Without it every adopted role would need its own vertical
 * fudge at the point of use, which is exactly the pack-specific knowledge this
 * layer exists to keep out of the renderer.
 */
function normalise(scene: THREE.Object3D, entry: AssetEntry): THREE.Group {
  if (entry.rotation) {
    scene.rotation.set(entry.rotation.x ?? 0, entry.rotation.y ?? 0, entry.rotation.z ?? 0);
  }
  if (entry.scale !== undefined) scene.scale.setScalar(entry.scale);
  scene.updateMatrixWorld(true);

  const box = new THREE.Box3().setFromObject(scene);
  if (!box.isEmpty()) {
    const centre = box.getCenter(new THREE.Vector3());
    scene.position.x -= centre.x;
    scene.position.z -= centre.z;
    // Standing on something, or being one. A mage is placed at its feet; a
    // thrown axe is drawn at the point the simulation says it is and spins about
    // its own middle, so basing it would hang it below itself.
    scene.position.y -= entry.anchor === 'centre' ? centre.y : box.min.y;
  }
  scene.position.y += entry.yOffset ?? 0;

  // The corrections go on the inner object and the wrapper carries the anchor,
  // so the origin stays at the base when the wrapper is later scaled to fit.
  // Applying both to one object would scale the offset along with the model.
  const wrapper = new THREE.Group();
  wrapper.add(scene);
  wrapper.updateMatrixWorld(true);
  return wrapper;
}

/** Scales a model to the extents it is being placed into. */
function fitInto(
  object: THREE.Object3D, mode: FitMode, size?: { x: number; y: number; z: number },
): void {
  if (mode === 'none' || !size) return;

  object.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(object);
  const extent = box.getSize(new THREE.Vector3());
  if (extent.x <= 0 || extent.y <= 0 || extent.z <= 0) return;

  if (mode === 'stretch') {
    object.scale.set(
      object.scale.x * (size.x / extent.x),
      object.scale.y * (size.y / extent.y),
      object.scale.z * (size.z / extent.z),
    );
    return;
  }
  if (mode === 'fitXZ') {
    const footprint = Math.min(size.x / extent.x, size.z / extent.z);
    object.scale.set(
      object.scale.x * footprint,
      object.scale.y * (size.y / extent.y),
      object.scale.z * footprint,
    );
    return;
  }
  const uniform = Math.min(size.x / extent.x, size.y / extent.y, size.z / extent.z);
  object.scale.multiplyScalar(uniform);
}

/**
 * Applies a role's material corrections, once, to the shared scene.
 *
 * Materials are cloned first because a pack routinely shares one material
 * between several meshes and several models; grading one in place would grade
 * whatever else happened to reference it.
 */
function applyMaterials(scene: THREE.Object3D, o: MaterialOverrides): void {
  scene.traverse((node) => {
    const mesh = node as THREE.Mesh;
    if (!mesh.isMesh) return;
    const graded = (m: THREE.Material): THREE.Material => {
      const mat = m.clone() as THREE.MeshStandardMaterial;

      if (o.color !== undefined) mat.color = new THREE.Color(o.color);
      if (mat.color) {
        // Through HSL, so desaturating leaves the hue where the artist put it
        // and only takes the colour out of it.
        const hsl = { h: 0, s: 0, l: 0 };
        mat.color.getHSL(hsl);
        mat.color.setHSL(
          hsl.h,
          hsl.s * (1 - (o.desaturate ?? 0)),
          hsl.l * (o.darken ?? 1),
        );
      }

      if (o.roughness !== undefined) mat.roughness = o.roughness;
      if (o.metalness !== undefined) mat.metalness = o.metalness;
      if (o.emissive !== undefined) mat.emissive = new THREE.Color(o.emissive);
      if (o.emissiveIntensity !== undefined) mat.emissiveIntensity = o.emissiveIntensity;
      if (o.flatShading !== undefined) mat.flatShading = o.flatShading;

      // A pack's baked lighting is usually what fights your own hardest.
      if (o.dropMaps) {
        mat.map = null;
        mat.aoMap = null;
        mat.lightMap = null;
        mat.emissiveMap = null;
      }

      mat.needsUpdate = true;
      return mat;
    };
    mesh.material = Array.isArray(mesh.material)
      ? mesh.material.map(graded)
      : graded(mesh.material);
  });
}

/**
 * Visits every material under an object.
 *
 * A fallback is one mesh with one material; a loaded model is a tree with any
 * number of both. Call sites that used to reach straight for `mesh.material`
 * have to go through this instead, or they work on the gray-box and silently
 * stop working the day a model is dropped in behind the same role.
 */
export function eachMaterial(
  object: THREE.Object3D, fn: (m: THREE.MeshStandardMaterial) => void,
): void {
  object.traverse((node) => {
    const mesh = node as THREE.Mesh;
    if (!mesh.isMesh) return;
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const m of materials) fn(m as THREE.MeshStandardMaterial);
  });
}

/**
 * Recolours a model, cloning its materials so siblings are unaffected.
 *
 * `only` names the materials that carry faction colour. Without it every
 * material is recoloured, which is right for a gray-box capsule and disastrous
 * for a textured character — the tint multiplies against the map and turns a
 * Chaos mage into a red silhouette with the artwork stamped out.
 *
 * Matched as a prefix, because Blender appends `.001` and `.002` when one
 * material is used on several surfaces: a robe, sleeves and a hood arrive as
 * three names for the same intent.
 */
function tint(
  object: THREE.Object3D, colour: number, entry?: AssetEntry, skip?: Set<string>,
): void {
  const only = entry?.tintMaterial;
  const glow = entry?.tintEmissive ?? 0;
  const flat = entry?.tintFlat ?? false;

  object.traverse((node) => {
    const mesh = node as THREE.Mesh;
    if (!mesh.isMesh) return;
    // Cloned rather than mutated: materials are shared between every instance of
    // a model, so tinting one in place would recolour the whole faction.
    const recolour = (m: THREE.Material): THREE.Material => {
      /*
       * An authored model is never blanket-tinted.
       *
       * Without `tintMaterial` this function used to recolour *every* material it
       * found, which is right for a procedural capsule — the whole thing is the
       * faction — and wrong for anything an artist made, because nothing here can
       * tell a robe from a face. Three of the four mages had no `tintMaterial`,
       * and their garments were already dressed by `factionTextures` and so were
       * skipped: the only materials left for the blanket tint to reach were hair,
       * moustache, skin and eyes. Hence Chaos arcanists with red hair.
       *
       * The Cleric was spared only because it names `MW_Faction` for its scarf.
       * So the rule is that a model with a file behind it says which materials
       * carry faction colour, or none of them do.
       */
      if (!only && entry?.url) return m;
      if (only && !m.name?.startsWith(only)) return m;
      // Already wearing this faction's colours. A tint on top would only dull them.
      if (skip && [...skip].some((prefix) => m.name?.startsWith(prefix))) return m;
      const copy = m.clone() as THREE.MeshStandardMaterial;
      if (copy.color) copy.color = new THREE.Color(colour);

      // Carrying its own colour rather than waiting for a torch to find it.
      if (glow > 0) {
        copy.emissive = new THREE.Color(colour);
        copy.emissiveIntensity = glow;
        copy.emissiveMap = null;
      }
      if (flat) {
        copy.map = null;
        copy.aoMap = null;
      }
      copy.needsUpdate = true;
      return copy;
    };
    mesh.material = Array.isArray(mesh.material)
      ? mesh.material.map(recolour)
      : recolour(mesh.material);
  });
}

/**
 * Problems worth catching before they reach a screen.
 *
 * These are the failures that show up as an invisible mage or a pillar the size
 * of the cathedral rather than as an error, so they are worth asserting on.
 */
export function validateManifest(
  manifest: Partial<Record<string, AssetEntry>>,
): string[] {
  const problems: string[] = [];
  const roles = new Set<string>(ASSET_ROLES);

  for (const [role, entry] of Object.entries(manifest)) {
    if (!entry) continue;
    if (!roles.has(role)) problems.push(`${role} is not a known role`);
    if (!entry.url) problems.push(`${role} has no url`);
    else if (!entry.url.startsWith('/')) problems.push(`${role} url should be absolute: ${entry.url}`);
    if (entry.scale !== undefined && !(entry.scale > 0)) {
      problems.push(`${role} has a scale of ${entry.scale}`);
    }
    const m = entry.materials;
    if (m?.desaturate !== undefined && (m.desaturate < 0 || m.desaturate > 1)) {
      problems.push(`${role} desaturate should be 0..1, got ${m.desaturate}`);
    }
    if (m?.darken !== undefined && !(m.darken > 0)) {
      problems.push(`${role} darken should be above zero, got ${m.darken}`);
    }
    /*
     * A level asset has to say how it relates to the box it stands in, because
     * getting that wrong is silent: it renders, it is just the wrong size.
     *
     * `'none'` used to be the wrong answer here, back when the only level role
     * was the pillar and every one of them was stretched to fit. It is now a
     * legitimate one — shelves and crates keep the size they were modelled at
     * and are *repeated* along the box instead, which is the whole reason a run
     * of shelving can be drawn as one box in Godot. So the requirement is that
     * the entry states a mode, not that it states a particular one.
     */
    if (entry.fit === undefined && entry.url && role.startsWith('level.')) {
      problems.push(`${role} is placed into a box but has no fit mode`);
    }
    /*
     * A stride speed for a clip the role does not have is a silent no-op — the
     * animator looks it up by state, finds nothing for the state it is actually
     * playing, and plays that one straight. Which looks exactly like having
     * forgotten to fill the table in, so it is worth being told.
     */
    for (const [state, speed] of Object.entries(entry.clipSpeeds ?? {})) {
      if (!(speed > 0)) {
        problems.push(`${role} clipSpeeds.${state} should be above zero, got ${speed}`);
      }
      if (entry.clips && !(state in entry.clips)) {
        problems.push(`${role} has a stride speed for ${state} but no such clip`);
      }
    }
  }
  return problems;
}
